/**
 * operator-permission-groups — the canonical permission catalog + the
 * route→permission map. Single source of truth shared by admin-api, the
 * Lambda authorizer, and admin-ui.
 *
 * Granularity: ONE "can access" toggle per admin feature (every menu 大項
 * + 細項 + character-edit tab + admin/billing/system section). Keys are
 * stable identifiers — never derive them from labels.
 *
 * `superadminDefault: true` marks features that are superadmin-only today;
 * the seeded default `operator` group EXCLUDES these (so existing operators
 * keep exactly their current reach), but a superadmin MAY grant them to any
 * group.
 */
import { z } from 'zod';
export const PERMISSION_CATALOG_VERSION = 'perm-catalog-v1';
export const PERMISSIONS = [
    // ── 儀表板 / 重大訊息 ──────────────────────────────────────────
    { key: 'dashboard.view', label: '儀表板', category: '儀表板', superadminDefault: false },
    { key: 'incidents.view', label: '重大訊息', category: '重大訊息', superadminDefault: false },
    // ── 角色經營 ─────────────────────────────────────────────────
    { key: 'char.list', label: '角色清單 / 編輯', category: '角色經營', superadminDefault: false },
    { key: 'char.world', label: '角色世界', category: '角色經營', superadminDefault: false },
    { key: 'char.automation-runs', label: '自動化歷程', category: '角色經營', superadminDefault: false },
    { key: 'char.stage', label: '直播舞台', category: '角色經營', superadminDefault: false },
    { key: 'char.point-cards', label: '點數卡', category: '角色經營', superadminDefault: false },
    { key: 'char.library', label: '物件庫', category: '角色經營', superadminDefault: false },
    // ── 角色編輯(編輯分頁,細項)─────────────────────────────────
    { key: 'char.edit.overview', label: '編輯:Overview', category: '角色編輯', superadminDefault: false },
    { key: 'char.edit.layers', label: '編輯:Layers', category: '角色編輯', superadminDefault: false },
    { key: 'char.edit.video', label: '編輯:影片匯入', category: '角色編輯', superadminDefault: false },
    { key: 'char.edit.expressions', label: '編輯:Expressions', category: '角色編輯', superadminDefault: false },
    { key: 'char.edit.mouth-blink', label: '編輯:Mouth/Blink', category: '角色編輯', superadminDefault: false },
    { key: 'char.edit.ai', label: '編輯:AI', category: '角色編輯', superadminDefault: false },
    { key: 'char.edit.audio-clips', label: '編輯:自訂聲音', category: '角色編輯', superadminDefault: false },
    { key: 'char.edit.shortcuts', label: '編輯:快捷鍵', category: '角色編輯', superadminDefault: false },
    { key: 'char.edit.preview', label: '編輯:Preview', category: '角色編輯', superadminDefault: false },
    { key: 'char.edit.fans', label: '編輯:粉絲', category: '角色編輯', superadminDefault: false },
    { key: 'char.edit.relationships', label: '編輯:粉絲關係', category: '角色編輯', superadminDefault: false },
    { key: 'char.edit.workflows', label: '編輯:自動化', category: '角色編輯', superadminDefault: false },
    { key: 'char.edit.brand-assets', label: '編輯:品牌素材', category: '角色編輯', superadminDefault: false },
    { key: 'char.edit.youtube', label: '編輯:頻道整合', category: '角色編輯', superadminDefault: false },
    { key: 'char.edit.deploy', label: '編輯:部署', category: '角色編輯', superadminDefault: false },
    { key: 'char.edit.actions', label: '編輯:動作 (3D)', category: '角色編輯', superadminDefault: false },
    { key: 'char.edit.render-params', label: '編輯:渲染參數 (3D)', category: '角色編輯', superadminDefault: false },
    { key: 'char.edit.textures', label: '編輯:貼圖對應 (3D)', category: '角色編輯', superadminDefault: false },
    { key: 'char.edit.voice', label: '編輯:聲音 (3D)', category: '角色編輯', superadminDefault: false },
    { key: 'char.edit.objects', label: '編輯:物件 (3D)', category: '角色編輯', superadminDefault: false },
    { key: 'char.edit.live2d', label: '編輯:Live2D 設定', category: '角色編輯', superadminDefault: false },
    { key: 'char.edit.graph', label: '編輯:粉絲關聯圖譜', category: '角色編輯', superadminDefault: false },
    { key: 'char.edit.audit', label: '編輯:粉絲對話紀錄', category: '角色編輯', superadminDefault: false },
    // ── 粉絲營運 ─────────────────────────────────────────────────
    { key: 'fans.list', label: '粉絲清單', category: '粉絲營運', superadminDefault: false },
    { key: 'fans.list-edit', label: '粉絲清單:編輯(管理員備註)', category: '粉絲營運', superadminDefault: true },
    { key: 'fans.detail', label: '粉絲詳情', category: '粉絲營運', superadminDefault: false },
    { key: 'fans.gift', label: '贈送點數(禮物)', category: '粉絲營運', superadminDefault: true },
    { key: 'fan-groups.manage', label: '粉絲群組', category: '粉絲營運', superadminDefault: false },
    { key: 'activities.manage', label: '活動', category: '粉絲營運', superadminDefault: false },
    { key: 'audit.view', label: '對話紀錄', category: '粉絲營運', superadminDefault: false },
    // fan-game-bridge — 外部小遊戲註冊表(superadmin-only 管理)。
    { key: 'games.manage', label: '遊戲註冊表', category: '粉絲營運', superadminDefault: true },
    // ── 平台帳務(目前 superadmin-only)──────────────────────────
    { key: 'billing.topups', label: '儲值訂單', category: '平台帳務', superadminDefault: true },
    { key: 'billing.topup-packages', label: '儲值套餐', category: '平台帳務', superadminDefault: true },
    { key: 'billing.operator-cuts', label: '平台抽成', category: '平台帳務', superadminDefault: true },
    { key: 'billing.payouts', label: '出金審核', category: '平台帳務', superadminDefault: true },
    { key: 'billing.traffic', label: '流量計費', category: '平台帳務', superadminDefault: true },
    // ── 使用者管理(superadmin-only)─────────────────────────────
    { key: 'users.manage', label: '使用者管理', category: '使用者管理', superadminDefault: true },
    { key: 'users.access', label: '角色存取權', category: '使用者管理', superadminDefault: true },
    { key: 'perm-groups.manage', label: '權限群組管理', category: '使用者管理', superadminDefault: true },
    // superadmin-fan-management — 平台級、以粉絲為核心的統一管理檢視
    // (全平台粉絲目錄 + 單一粉絲的全部儲值 + 跟每一隻角色的對話)。
    { key: 'fans.management', label: '粉絲管理', category: '使用者管理', superadminDefault: true },
    // ── 平台 LLM(superadmin-only)───────────────────────────────
    { key: 'platform.llm', label: '平台 LLM', category: '平台 LLM', superadminDefault: true },
    // ── 系統管理 ─────────────────────────────────────────────────
    { key: 'operator.profile', label: '經營者設定', category: '系統管理', superadminDefault: false },
    { key: 'operator.earnings', label: '我的收益', category: '系統管理', superadminDefault: false },
    { key: 'operator.payouts', label: '申請出金', category: '系統管理', superadminDefault: false },
    { key: 'voice-experiments.view', label: '語音對話實驗', category: '系統管理', superadminDefault: false },
    { key: 'operator-wallet.view', label: '經營者錢包', category: '系統管理', superadminDefault: true },
    { key: 'operator-approvals.manage', label: '經營者審查', category: '系統管理', superadminDefault: true },
    { key: 'api-docs.view', label: 'API 文件', category: '系統管理', superadminDefault: true },
    { key: 'system.settings', label: '系統設定', category: '系統管理', superadminDefault: true },
    // ── 開發工具(superadmin-only)──────────────────────────────
    { key: 'dev.world', label: '3D 開放世界', category: '開發工具', superadminDefault: true },
    { key: 'dev.v2-playground', label: 'v2 Playground', category: '開發工具', superadminDefault: true },
];
export const PERMISSION_KEYS = PERMISSIONS.map((p) => p.key);
const PERMISSION_KEY_SET = new Set(PERMISSION_KEYS);
/** Keys the seeded default `operator` group receives (everything that is
 *  NOT superadmin-only today) — so existing operators keep their reach. */
export function operatorDefaultPermissions() {
    return PERMISSIONS.filter((p) => !p.superadminDefault).map((p) => p.key);
}
export function isKnownPermission(key) {
    return PERMISSION_KEY_SET.has(key);
}
// ── Route → permission map ──────────────────────────────────────────
/** Sentinel: any authenticated operator may call (no specific permission).
 *  Unmapped routes default to this. */
export const PUBLIC_AUTHED = '__public_authed__';
/** Sentinel: a fan-pool route — operator-permission enforcement skips it
 *  (fans are gated by userKind elsewhere). */
export const FAN_ROUTE = '__fan__';
/**
 * Ordered, most-specific-first. Only the genuinely-gated routes are listed;
 * everything else falls through to PUBLIC_AUTHED. Fan routes are marked so
 * operator-permission logic ignores them.
 */
export const ROUTE_PERMISSIONS = [
    // Fans pool — skip operator-permission logic entirely.
    { method: '*', pathPattern: '/api/fan/{proxy+}', permission: FAN_ROUTE },
    // me — every operator needs this to load their menu.
    { method: 'GET', pathPattern: '/api/me/permissions', permission: PUBLIC_AUTHED },
    // 平台帳務.
    { method: '*', pathPattern: '/api/admin/topup-packages', permission: 'billing.topup-packages' },
    { method: '*', pathPattern: '/api/admin/topups/{proxy+}', permission: 'billing.topups' },
    { method: 'GET', pathPattern: '/api/admin/topups', permission: 'billing.topups' },
    { method: '*', pathPattern: '/api/admin/payouts/{proxy+}', permission: 'billing.payouts' },
    { method: 'GET', pathPattern: '/api/admin/payouts', permission: 'billing.payouts' },
    { method: 'GET', pathPattern: '/api/admin/revenue/{proxy+}', permission: 'billing.operator-cuts' },
    { method: 'GET', pathPattern: '/api/admin/billing/{proxy+}', permission: 'billing.traffic' },
    // character-energy-system — platform free-regen subsidy report + per-fan ledger.
    { method: 'GET', pathPattern: '/api/admin/energy/subsidy', permission: 'billing.traffic' },
    { method: 'GET', pathPattern: '/api/admin/energy/ledger', permission: 'billing.traffic' },
    // 使用者管理 + 權限群組.
    { method: '*', pathPattern: '/api/admin/permission-groups/{proxy+}', permission: 'perm-groups.manage' },
    { method: '*', pathPattern: '/api/admin/permission-groups', permission: 'perm-groups.manage' },
    { method: 'PATCH', pathPattern: '/api/admin/users/{userSub}/role', permission: 'users.manage' },
    { method: 'GET', pathPattern: '/api/admin/users', permission: 'users.manage' },
    { method: '*', pathPattern: '/api/admin/character-access', permission: 'users.access' },
    // superadmin-fan-management — whole namespace is superadmin-only
    // (fans.management is superadminDefault, and each handler re-checks
    // isSuperadmin). Must precede the broader /api/admin/fans matches.
    { method: '*', pathPattern: '/api/admin/fan-management/{proxy+}', permission: 'fans.management' },
    // 平台 LLM + 經營者審查 + 系統設定.
    { method: '*', pathPattern: '/api/admin/platform-settings/{proxy+}', permission: 'platform.llm' },
    { method: '*', pathPattern: '/api/admin/platform-settings', permission: 'platform.llm' },
    { method: '*', pathPattern: '/api/admin/operators/{id}/{proxy+}', permission: 'operator-approvals.manage' },
    { method: 'GET', pathPattern: '/api/admin/operators', permission: 'operator-approvals.manage' },
    { method: 'GET', pathPattern: '/api/admin/operators/resolve', permission: 'operator-approvals.manage' },
    { method: '*', pathPattern: '/api/admin/bone-labels', permission: 'system.settings' },
    // 粉絲營運.
    // 粉絲清單頁(新):view 用 fans.list,編輯備註用 fans.list-edit.
    { method: 'PATCH', pathPattern: '/api/admin/fan-list/{fanId}', permission: 'fans.list-edit' },
    { method: 'GET', pathPattern: '/api/admin/fan-list', permission: 'fans.list' },
    { method: 'POST', pathPattern: '/api/admin/fans/{fanId}/gift', permission: 'fans.gift' },
    { method: 'GET', pathPattern: '/api/admin/fans/{proxy+}', permission: 'fans.list' },
    { method: 'GET', pathPattern: '/api/admin/fans', permission: 'fans.list' },
    { method: '*', pathPattern: '/api/admin/groups/{proxy+}', permission: 'fan-groups.manage' },
    { method: '*', pathPattern: '/api/admin/groups', permission: 'fan-groups.manage' },
    { method: '*', pathPattern: '/api/admin/activities/{proxy+}', permission: 'activities.manage' },
    { method: '*', pathPattern: '/api/admin/activities', permission: 'activities.manage' },
    { method: 'GET', pathPattern: '/api/admin/audit/{proxy+}', permission: 'audit.view' },
    // fan-game-bridge — 遊戲註冊表(list/create/item/* 全在此 proxy 下)。
    { method: '*', pathPattern: '/api/admin/games/{proxy+}', permission: 'games.manage' },
    // 角色經營.
    { method: '*', pathPattern: '/api/admin/point-cards/{proxy+}', permission: 'char.point-cards' },
    { method: '*', pathPattern: '/api/admin/stages/{proxy+}', permission: 'char.stage' },
    { method: '*', pathPattern: '/api/library/objects/{proxy+}', permission: 'char.library' },
    { method: '*', pathPattern: '/api/library/objects', permission: 'char.library' },
    // 系統管理 (operator-visible).
    { method: '*', pathPattern: '/api/operator/earnings/{proxy+}', permission: 'operator.earnings' },
    { method: 'GET', pathPattern: '/api/operator/earnings', permission: 'operator.earnings' },
    { method: '*', pathPattern: '/api/operator/payouts', permission: 'operator.payouts' },
    // /api/operator/profile 故意不設權限(PUBLIC_AUTHED):新註冊經營者尚未通過
    // 審查、不在任何 Cognito 群組,但必須能呼叫此路由才會 lazy-create pending
    // 列(出現在經營者審查佇列)並完成首次登入填資料。把它鎖進 operator.profile
    // 會造成「待審者永遠進不了審查佇列」的死結。寫入面仍由 handler 的
    // isOperator + requireApprovedOperator 把關;選單顯示仍用 operator.profile key。
    { method: '*', pathPattern: '/api/admin/voice-experiments/{proxy+}', permission: 'voice-experiments.view' },
    { method: 'GET', pathPattern: '/api/admin/voice-experiments', permission: 'voice-experiments.view' },
];
/** Match an incoming method + path to a required permission. First match
 *  wins; unmapped → PUBLIC_AUTHED. `{param}` matches one segment;
 *  `{proxy+}` matches one-or-more trailing segments. */
export function resolveRoutePermission(method, path) {
    const reqSegs = path.split('/').filter(Boolean);
    for (const r of ROUTE_PERMISSIONS) {
        if (r.method !== '*' && r.method.toUpperCase() !== method.toUpperCase())
            continue;
        if (matchPattern(r.pathPattern, reqSegs))
            return r.permission;
    }
    return PUBLIC_AUTHED;
}
function matchPattern(pattern, reqSegs) {
    const patSegs = pattern.split('/').filter(Boolean);
    for (let i = 0; i < patSegs.length; i++) {
        const p = patSegs[i];
        if (p === '{proxy+}')
            return reqSegs.length >= i + 1; // matches rest
        if (i >= reqSegs.length)
            return false;
        if (p.startsWith('{') && p.endsWith('}'))
            continue; // single-segment param
        if (p !== reqSegs[i])
            return false;
    }
    return patSegs.length === reqSegs.length;
}
// ── Permission group schema ─────────────────────────────────────────
export const PermissionGroupSchema = z.object({
    groupId: z.string().min(1).max(128).regex(/^[A-Za-z0-9_.@-]+$/),
    name: z.string().min(1).max(80),
    description: z.string().max(280).optional(),
    permissions: z.array(z.string()).refine((arr) => arr.every(isKnownPermission), { message: 'permissions must all be known catalog keys' }),
    updatedAt: z.string().datetime(),
});
export const PermissionGroupCreateSchema = PermissionGroupSchema.omit({ updatedAt: true });
export const PermissionGroupPatchSchema = z.object({
    name: z.string().min(1).max(80).optional(),
    description: z.string().max(280).nullable().optional(),
    permissions: z.array(z.string()).refine((arr) => arr.every(isKnownPermission), { message: 'permissions must all be known catalog keys' }).optional(),
});
